import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import { BinIcon, EditAppointmentIcon } from 'src/icons';
import {
  createAgentSkill,
  deleteAgentSkill,
  fetchAgentSkills,
  updateAgentSkill,
  AgentSkill,
} from 'src/modules/SuperAdmin/services/agentSkillsApi';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';

type AgentSkillsSectionProps = {
  orgUuid: string;
  agentId: string;
};

type FormValues = {
  name: string;
  description: string;
  is_supported: boolean;
};

const AgentSkillsSection = ({ orgUuid, agentId }: AgentSkillsSectionProps) => {
  const [skills, setSkills] = useState<AgentSkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<AgentSkill | null>(null);
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadSkills = useCallback(async () => {
    if (!orgUuid || !agentId) return;
    setLoading(true);
    try {
      const list = await fetchAgentSkills(orgUuid, agentId);
      setSkills(list);
    } catch {
      showToastError('Failed to load agent skills');
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }, [orgUuid, agentId]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const openAdd = () => {
    setEditingSkill(null);
    form.resetFields();
    form.setFieldsValue({ is_supported: true });
    setFormModalOpen(true);
  };

  const openEdit = (record: AgentSkill) => {
    setEditingSkill(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description ?? '',
      is_supported: record.is_supported,
    });
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingSkill(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    if (!orgUuid || !agentId) {
      showToastError('Organization UUID is required');
      return;
    }
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      throw e;
    }
    setSubmitting(true);
    try {
      if (editingSkill) {
        await updateAgentSkill(orgUuid, editingSkill.agent_skill_id, {
          name: values.name.trim(),
          description: values.description?.trim() || null,
          is_supported: values.is_supported,
        });
        showToastSuccess('Skill updated');
      } else {
        await createAgentSkill(orgUuid, {
          agent_id: agentId,
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          is_supported: values.is_supported,
        });
        showToastSuccess('Skill created');
      }
      closeFormModal();
      await loadSkills();
    } catch {
      showToastError(
        editingSkill ? 'Failed to update skill' : 'Failed to create skill'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (skillId: string) => {
    if (!orgUuid) return;
    try {
      await deleteAgentSkill(orgUuid, skillId);
      showToastSuccess('Skill removed');
      await loadSkills();
    } catch {
      showToastError('Failed to remove skill');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Supported',
      dataIndex: 'is_supported',
      key: 'is_supported',
      width: 110,
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    {
      title: '',
      key: 'actions',
      width: 88,
      render: (_: unknown, record: AgentSkill) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              className="flex items-center justify-center"
              icon={<EditAppointmentIcon className="" onClick={() => {}} />}
              aria-label="Edit skill"
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Remove this skill?"
            onConfirm={() => handleDelete(record.agent_skill_id)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button
              type="text"
              size="small"
              danger
              className="flex items-center justify-center"
              icon={<BinIcon className="" onClick={() => {}} />}
              aria-label="Remove skill"
              title="Remove"
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold m-0">Skills</h3>
        <Button type="primary" size="small" onClick={openAdd}>
          Add skill
        </Button>
      </div>
      <Table
        size="small"
        rowKey="agent_skill_id"
        columns={columns}
        dataSource={skills}
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'No skills yet' }}
      />

      <Modal
        title={editingSkill ? 'Edit skill' : 'Add skill'}
        open={formModalOpen}
        onCancel={closeFormModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" className="mt-2">
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please enter a skill name' },
              { max: 255, message: 'Name must not exceed 255 characters' },
            ]}
          >
            <Input placeholder="Skill name" maxLength={255} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea
              rows={3}
              placeholder="Optional description"
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="is_supported"
            label="Supported"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentSkillsSection;
